import { Controller } from "@hotwired/stimulus"

// Converts Persian (۰-۹) and Arabic-Indic (٠-٩) digits to Western ones, then
// strips anything left that isn't 0-9 — so typing/pasting a code in any of
// the three numeral systems works the same.
function toDigits(value) {
  return value
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, "")
}

// OTP cell row: auto-advance on input, backspace to previous cell, paste
// distribution, combined value mirrored into a hidden input.
//
// When the code is complete (typed, pasted, or auto-read from SMS) the
// surrounding form is submitted automatically unless auto-submit is off.
//
// SMS auto-read uses the WebOTP API (Chrome on Android). The SMS must end
// with the origin-bound line:  @your-domain.com #123456
//
// Dispatches: otp:complete -> detail: { code }  (when all cells are filled)
export default class extends Controller {
  static targets = ["cell", "input"]

  static values = {
    autoSubmit: { type: Boolean, default: true }
  }

  connect() {
    this._submitted = false
    this._listenForSms()
  }

  disconnect() {
    this._smsAbort?.abort()
  }

  input(event) {
    const cell = event.currentTarget
    cell.value = toDigits(cell.value).slice(-1)
    cell.toggleAttribute("data-filled", cell.value !== "")

    if (cell.value) {
      const next = this.cellTargets[this.cellTargets.indexOf(cell) + 1]
      next?.focus()
    }
    this._sync()
  }

  keydown(event) {
    const cell = event.currentTarget
    if (event.key === "Backspace" && !cell.value) {
      const prev = this.cellTargets[this.cellTargets.indexOf(cell) - 1]
      if (prev) {
        prev.value = ""
        prev.removeAttribute("data-filled")
        prev.focus()
        event.preventDefault()
        this._sync()
      }
    }
  }

  paste(event) {
    event.preventDefault()
    const digits = toDigits(event.clipboardData?.getData("text") || "")
    if (!digits) return

    // A full code replaces everything; a partial one fills from the cursor
    if (digits.length >= this.cellTargets.length) {
      this._fill(digits)
      return
    }

    const start = this.cellTargets.indexOf(event.currentTarget)
    this.cellTargets.slice(start).forEach((cell, i) => {
      cell.value = digits[i] ?? cell.value
      cell.toggleAttribute("data-filled", cell.value !== "")
    })
    this.cellTargets[Math.min(start + digits.length, this.cellTargets.length - 1)]?.focus()
    this._sync()
  }

  // Private

  // Auto-read the code from an incoming SMS via the WebOTP API
  _listenForSms() {
    if (!("OTPCredential" in window)) return

    this._smsAbort = new AbortController()
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: this._smsAbort.signal })
      .then((credential) => {
        if (credential?.code) this._fill(credential.code)
      })
      .catch(() => {}) // aborted on disconnect, denied, or timed out — ignore
  }

  _fill(code) {
    const digits = toDigits(code)
    this.cellTargets.forEach((cell, i) => {
      cell.value = digits[i] ?? ""
      cell.toggleAttribute("data-filled", cell.value !== "")
    })
    this._sync()
  }

  _sync() {
    const code = this.cellTargets.map((c) => c.value).join("")
    if (this.hasInputTarget) this.inputTarget.value = code

    if (code.length < this.cellTargets.length) {
      this._submitted = false
      return
    }
    if (this._submitted) return

    this._submitted = true
    this.dispatch("complete", { detail: { code } })
    if (this.autoSubmitValue) this.element.closest("form")?.requestSubmit()
  }
}
